'use client';

import { useEffect, useState } from "react";
import { GiftBoxBlockSelector } from "./GiftBoxBlockSelector";
import { Box, Button, Stack, Text } from "@chakra-ui/react";
import { AddToCartDto } from "@/types/cart";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { addToCartRequest } from "@/services/cart";
import { openDrawer } from "@/shared/store/drawer";
import { GiftBox, GiftBoxBlock } from "@/types/giftbox";
import { useTranslations } from "next-intl";
// import { addToCart } from "@/shared/store/cart"; // если есть такой метод

interface Props {
  blocks: GiftBox['blocks'];
  onSelectionsChange?: (selections: Record<string, {
    selectedIndex: number;
    selectedSize: string | null;
    isValid: boolean;
  }>, currentPrice?: number) => void;
  giftboxData: GiftBox;
}

export function GiftBoxSelectorGroup({
  blocks,
  onSelectionsChange,
  giftboxData,
}: Props) {
  const t = useTranslations();
  const [selections, setSelections] = useState<Record<string, {
    selectedIndex: number;
    selectedSize: string | null;
    isValid: boolean;
  }>>({});

  const queryClient = useQueryClient();

  const handleSelectionChange = (update: {
    blockId: string;
    selectedIndex: number;
    selectedSize: string | null;
    isValid: boolean;
  }) => {
    setSelections(prev => {
      const newSelections = {
        ...prev,
        [update.blockId]: {
          selectedIndex: update.selectedIndex,
          selectedSize: update.selectedSize,
          isValid: update.isValid,
        },
      };

      const boxPrice =
        typeof giftboxData.boxProduct?.price === "number"
          ? giftboxData.boxProduct.price
          : 0;

      const selectionsPrice = blocks.reduce((sum, block) => {
        const sel = newSelections[block.id];
        if (!sel) return sum;

        const item = block.items.filter((i) => i.product)[sel.selectedIndex];
        if (!item || !item.product) return sum;

        return sum + item.product.price;
      }, 0);

      const newPrice = boxPrice + selectionsPrice;

      onSelectionsChange?.(newSelections, newPrice);
      return newSelections;
    });

  };

  const allValid = blocks.every(block => selections[block.id]?.isValid);
  useEffect(() => {
    console.debug("Текущие выборы в гифтбоксе:", selections);
    console.debug("Валидны ли все выборы:", allValid);
  }, [selections]);

  const addToCartMutation = useMutation({
    mutationFn: async (dto: AddToCartDto) => {
      await addToCartRequest(dto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cart"] });
      openDrawer({ type: "main", tab: "cart" });
    },
  });

  const handleAddToCart = () => {
    const selectedItems = blocks.map(block => {
      const sel = selections[block.id];
      const item = block.items.filter((i) => i.product)[sel.selectedIndex];

      return {
        blockId: block.id,
        productId: item.product.id,
        sizeName: sel.selectedSize || null,
      };
    });

    const dto: AddToCartDto = {
      amount: 1,
      giftBoxId: giftboxData.id,
      selectedItems,
    };

    addToCartMutation.mutate(dto);
    console.debug("Добавляем в корзину:", dto);
  };

  return (
    <Stack gap={6} flex={1} align={"stretch"}>
      <Stack gap={{ base: "16px", md: "24px" }} direction={{ base: "row", md: "column" }} overflowX={{ base: "auto", md: "visible" }}>
        {blocks.map(block => (
          <GiftBoxBlockSelector
            key={block.id}
            block={block}
            onSelectionChange={handleSelectionChange}
          />
        ))}
      </Stack>


      <Button
        w={"full"}
        size="lg"
        height={"56px"}
        borderRadius={"none"}
        onClick={handleAddToCart}
        disabled={!allValid}
      >
        <Text textStyle={"bold16"}>
          {t('add_to_cart')}
        </Text>
      </Button>
    </Stack>
  );
}
